import data from "./generated-catalog.json";
import { cleanPublicText, fixMaybeBrokenText } from "@/lib/text-shared";

export type Category = {
  id: number;
  parentId: number | null;
  slug: string;
  fullSlug: string;
  name: string;
  level: number;
  isTopStatic: boolean;
  sortOrder: number;
  productCount: number;
  directProductCount?: number;
  previewImage?: string | null;
};

export type Product = {
  id: number;
  slug: string;
  sku: string | null;
  name: string;
  categoryId: number | null;
  categorySlug: string | null;
  categoryFullSlug: string | null;
  price: number;
  oldPrice: number;
  stockQty: number;
  rentalDayPrice?: number | null;
  rentalWeekPrice?: number | null;
  rentalMonthPrice?: number | null;
  unit: string | null;
  shortDescription: string | null;
  fullDescription: string | null;
  pageTitle: string | null;
  metaDescription: string | null;
  promoLabel?: string | null;
  promoKind?: string | null;
  isHidden?: boolean;
  isRentalOnly?: boolean;
  primaryImage: string | null;
  imageAltText?: string | null;
  images: string[];
  attributes: Array<{ name: string; value: string }>;
  variantGroup?: string | null;
  relatedProductSlugs?: string[];
};

export type CategoryNode = Category & {
  children: CategoryNode[];
};

function normalizeProductText(product: Product): Product {
  return {
    ...product,
    shortDescription: cleanPublicText(product.shortDescription) || null,
    fullDescription: cleanPublicText(product.fullDescription) || null,
    metaDescription: cleanPublicText(product.metaDescription) || null
  };
}

const categories = fixMaybeBrokenText(data.categories as Category[]);
const products = fixMaybeBrokenText((data.products as Product[]).filter((item) => !item.isHidden)).map(
  normalizeProductText
);

export { categories, products };

const childrenByParent = new Map<number | null, Category[]>();

for (const category of categories) {
  const list = childrenByParent.get(category.parentId) ?? [];
  list.push(category);
  childrenByParent.set(category.parentId, list);
}

for (const list of childrenByParent.values()) {
  list.sort((a, b) => a.sortOrder - b.sortOrder);
}

function buildNode(category: Category, sourceChildren: Map<number | null, Category[]>): CategoryNode {
  return {
    ...category,
    children: (sourceChildren.get(category.id) ?? []).map((child) => buildNode(child, sourceChildren))
  };
}

export function buildCategoryTree(sourceCategories: Category[]) {
  const grouped = new Map<number | null, Category[]>();

  for (const category of sourceCategories) {
    const list = grouped.get(category.parentId) ?? [];
    list.push(category);
    grouped.set(category.parentId, list);
  }

  for (const list of grouped.values()) {
    list.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  return (grouped.get(null) ?? []).map((category) => buildNode(category, grouped));
}

export function getFeaturedProducts(limit = 8) {
  return products.slice(0, limit);
}

export function getTopCategories() {
  return categories.filter((item) => item.isTopStatic);
}

export function getRootCategoryTree() {
  return buildCategoryTree(categories);
}

export function getCategoryBySlug(slug: string) {
  return categories.find((item) => item.slug === slug);
}

export function getCategoryChildrenById(id: number) {
  return childrenByParent.get(id) ?? [];
}

export function getCategoryChildrenBySlug(slug: string) {
  const category = getCategoryBySlug(slug);
  return category ? getCategoryChildrenById(category.id) : [];
}

export function getProductsByCategory(slug: string) {
  const category = getCategoryBySlug(slug);
  if (!category) {
    return [];
  }

  return products.filter(
    (item) =>
      item.categorySlug === slug ||
      item.categoryFullSlug === category.fullSlug ||
      item.categoryFullSlug?.startsWith(`${category.fullSlug}/`)
  );
}

export function getProductBySlug(slug: string) {
  return products.find((item) => item.slug === slug);
}

export function formatPrice(value: number) {
  return `${new Intl.NumberFormat("ru-RU").format(value)} ₽`;
}

function normalizeAssetPath(src: string) {
  if (!src) {
    return src;
  }

  if (src.startsWith("//")) {
    return `https:${src}`;
  }

  return src;
}

function getLocalStorelandPath(src: string) {
  try {
    const parsed = new URL(normalizeAssetPath(src));
    if (!/storeland\.(?:net|ru)$/i.test(parsed.hostname)) {
      return src;
    }

    return `/uploads/storeland/${parsed.hostname}/${parsed.pathname.replace(/^\/+/, "")}`.replace(/\/{2,}/g, "/");
  } catch {
    return src;
  }
}

export function resolveCatalogAsset(src: string | null | undefined) {
  if (!src) {
    return "/placeholder-product.png";
  }

  const normalized = normalizeAssetPath(src);
  if (/^https?:\/\//i.test(normalized) && /storeland\.(?:net|ru)/i.test(normalized)) {
    return getLocalStorelandPath(normalized);
  }

  return normalized;
}

export function pickBestProductImage(primaryImage: string | null | undefined, images: string[] = []) {
  const normalizedPrimary = primaryImage ? resolveCatalogAsset(primaryImage) : null;
  const normalizedGallery = images.filter(Boolean).map((src) => resolveCatalogAsset(src)) as string[];
  const galleryFallbacks = normalizedGallery.filter((src) => !src.startsWith("/uploads/products/"));

  const prioritized =
    normalizedPrimary?.startsWith("/uploads/products/") && galleryFallbacks.length
      ? [...galleryFallbacks, normalizedPrimary, ...normalizedGallery]
      : [normalizedPrimary, ...normalizedGallery];

  const candidates = prioritized
    .filter(Boolean)
    .filter((src, index, list) => list.indexOf(src) === index) as string[];

  return candidates[0] ?? "/placeholder-product.png";
}

export function getProductImage(product: Product) {
  return pickBestProductImage(product.primaryImage, product.images);
}

export function getProductImageAlt(product: Product) {
  return product.imageAltText?.trim() || product.name;
}

export function getProductGalleryImages(product: Product) {
  const primary = pickBestProductImage(product.primaryImage, product.images);
  const unique = Array.from(
    new Set(
      [primary, product.primaryImage, ...product.images]
        .filter(Boolean)
        .map((src) => resolveCatalogAsset(src)) as string[]
    )
  );

  return unique.length ? unique : ["/placeholder-product.png"];
}

export function getCategoryPreviewImage(slug: string) {
  const category = getCategoryBySlug(slug);
  if (category?.previewImage?.trim()) {
    return resolveCatalogAsset(category.previewImage);
  }

  const product = getProductsByCategory(slug)[0];
  return product ? getProductImage(product) : "/brand-logo.png";
}
